import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "@/firebase/config";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, Banknote, ShieldAlert, Calculator } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface FinanceSettingsProps {
  schoolId: string | null;
  onBack: () => void;
}

const FinanceSettings: React.FC<FinanceSettingsProps> = ({ schoolId, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allowCashPayment, setAllowCashPayment] = useState(false);
  
  // Fee Automation State
  const [termStartFee, setTermStartFee] = useState("full");
  const [termStartPartial, setTermStartPartial] = useState("50");
  const [termMidFee, setTermMidFee] = useState("partial");
  const [termMidPartial, setTermMidPartial] = useState("50");
  const [termEndFee, setTermEndFee] = useState("none");
  const [termEndPartial, setTermEndPartial] = useState("50");
  const [savingAssessment, setSavingAssessment] = useState(false);
  
  // Alert Dialog State
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showFeeConfirmDialog, setShowFeeConfirmDialog] = useState(false);
  const [pendingCashState, setPendingCashState] = useState(false);
  
  const { toast } = useToast();

  useEffect(() => {
    const fetchSettings = async () => {
      if (!schoolId) return;
      try {
        // Read from the shared payment methods config
        const cashRef = doc(db, `schools/${schoolId}/systemConfig/paymentMethods/list/cash`);
        const snap = await getDoc(cashRef);
        
        if (snap.exists()) {
          setAllowCashPayment(!!snap.data().enabled);
        } else {
          // If doc doesn't exist, default is based on fallback (assume enabled by default)
          setAllowCashPayment(true);
        }

        // Read the fee assessment configuration
        const feeAssessmentRef = doc(db, `schools/${schoolId}/systemConfig/feeAssessment`);
        const feeSnap = await getDoc(feeAssessmentRef);
        if (feeSnap.exists()) {
          const data = feeSnap.data();
          setTermStartFee(data.termStartFee || "full");
          setTermStartPartial(data.termStartPartial || "50");
          setTermMidFee(data.termMidFee || "partial");
          setTermMidPartial(data.termMidPartial || "50");
          setTermEndFee(data.termEndFee || "none");
          setTermEndPartial(data.termEndPartial || "50");
        }
      } catch (err) {
        console.error("Failed to fetch finance settings", err);
        toast({
          title: "Error",
          description: "Could not load settings.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, [schoolId, toast]);

  const handleToggleClick = (checked: boolean) => {
    setPendingCashState(checked);
    setShowConfirmDialog(true);
  };

  const confirmToggle = async () => {
    if (!schoolId) return;
    setSaving(true);
    try {
      // Save to shared payment methods list
      const cashRef = doc(db, `schools/${schoolId}/systemConfig/paymentMethods/list/cash`);
      await setDoc(cashRef, { enabled: pendingCashState }, { merge: true });
      
      setAllowCashPayment(pendingCashState);
      toast({
        title: "Settings Saved",
        description: `Cash payments have been ${pendingCashState ? 'enabled' : 'disabled'} for this school.`,
        className: pendingCashState ? "bg-green-50 text-green-900 border-green-200" : "bg-amber-50 text-amber-900 border-amber-200"
      });
      setShowConfirmDialog(false);
    } catch (err) {
      console.error("Failed to update settings", err);
      toast({
        title: "Update Failed",
        description: "An error occurred while saving your preferences.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const saveFeeAssessmentConfig = async () => {
    if (!schoolId) return;
    setSavingAssessment(true);
    try {
      const feeAssessmentRef = doc(db, `schools/${schoolId}/systemConfig/feeAssessment`);
      const payload = {
        termStartFee,
        termStartPartial,
        termMidFee,
        termMidPartial,
        termEndFee,
        termEndPartial
      };
      await setDoc(feeAssessmentRef, payload, { merge: true });
      setShowFeeConfirmDialog(false);
      toast({
        title: "Configuration Saved",
        description: "Fee assessment rules have been updated successfully.",
        className: "bg-indigo-50 text-indigo-900 border-indigo-200"
      });
    } catch (err) {
      console.error("Failed to save fee assessment rules", err);
      toast({
        title: "Save Failed",
        description: "Could not save fee assessment configuration.",
        variant: "destructive"
      });
    } finally {
      setSavingAssessment(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 h-full">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-transparent text-foreground overflow-y-auto p-4 sm:p-6">
      {/* Header Matching Finance Dashboard */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
        <Button 
          variant="outline"
          size="sm"
          onClick={onBack}
          className="gap-2 w-fit border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back</span>
        </Button>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
            Finance Settings
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configure default settings and payment methods for the school.
          </p>
        </div>
      </div>
      
      {/* Main Settings Content */}
      <div className="flex-1 max-w-4xl w-full mx-auto space-y-6">
        
        {/* Payment Methods Section */}
        <Card className="shadow-sm border-gray-200 dark:border-gray-800 w-full">
          <CardHeader className="border-b bg-gray-50/50 dark:bg-gray-900/50 pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Banknote className="h-5 w-5 text-green-600 dark:text-green-500" />
              Payment Methods
            </CardTitle>
            <CardDescription>
              Manage which channels parents can use to submit fee payments.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-lg border bg-current/5 hover:bg-current/10 border-gray-200 dark:border-gray-700 transition-colors">
              <div className="space-y-1 pr-6 flex-1">
                <Label className="text-base font-semibold text-foreground cursor-pointer">
                  Allow Cash Payments
                </Label>
                <p className="text-sm text-muted-foreground leading-snug">
                  When enabled, accountants can process standard physical cash transactions in the dashboard. Disabling this enforces digital-only (bank/M-Pesa) collections.
                </p>
              </div>
              <div className="shrink-0 flex items-center justify-end">
                <Switch 
                  checked={allowCashPayment}
                  onCheckedChange={handleToggleClick}
                  className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-gray-400 dark:data-[state=unchecked]:bg-gray-600"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Fee Assessment Automation Section */}
        <Card className="shadow-sm border-gray-200 dark:border-gray-800 w-full">
          <CardHeader className="border-b bg-gray-50/50 dark:bg-gray-900/50 pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calculator className="h-5 w-5 text-indigo-600 dark:text-indigo-500" />
              Fee Assessment Automation
            </CardTitle>
            <CardDescription>
              Configure how the system automatically calculates fees for students joining at different stages of the term.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            
            {/* Beginning of term */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 p-4 rounded-lg border bg-current/5 hover:bg-current/10 border-gray-200 dark:border-gray-700 transition-colors">
              <div className="space-y-1 pr-6 flex-1">
                <Label className="text-base font-semibold text-foreground">
                  Beginning of Term
                </Label>
                <p className="text-sm text-muted-foreground leading-snug">
                  What amount should a student be charged if they join at the beginning of the term?
                </p>
              </div>
              <div className="shrink-0 w-full sm:w-[180px] space-y-3">
                <Select value={termStartFee} onValueChange={setTermStartFee}>
                  <SelectTrigger className="w-full bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors shadow-sm focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/40 rounded-md h-10">
                    <SelectValue placeholder="Select amount" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-lg rounded-md overflow-hidden z-[100]">
                    <SelectItem value="full" className="cursor-pointer py-2 focus:bg-indigo-50 focus:text-indigo-900 dark:focus:bg-indigo-500/20 dark:focus:text-indigo-100 transition-colors">Full Payment</SelectItem>
                    <SelectItem value="partial" className="cursor-pointer py-2 focus:bg-indigo-50 focus:text-indigo-900 dark:focus:bg-indigo-500/20 dark:focus:text-indigo-100 transition-colors">Partial (Custom)</SelectItem>
                    <SelectItem value="none" className="cursor-pointer py-2 focus:bg-indigo-50 focus:text-indigo-900 dark:focus:bg-indigo-500/20 dark:focus:text-indigo-100 transition-colors">None (Next Term)</SelectItem>
                  </SelectContent>
                </Select>

                {termStartFee === 'partial' && (
                  <div className="flex items-center gap-2 pl-1 animate-in slide-in-from-top-2 fade-in duration-200">
                    <Label className="text-sm text-muted-foreground whitespace-nowrap">Charge %:</Label>
                    <Input
                      type="number"
                      min="1"
                      max="99"
                      value={termStartPartial}
                      onChange={(e) => setTermStartPartial(e.target.value)}
                      className="w-16 h-8 text-center bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 px-1"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Middle of term */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 p-4 rounded-lg border bg-current/5 hover:bg-current/10 border-gray-200 dark:border-gray-700 transition-colors">
              <div className="space-y-1 pr-6 flex-1">
                <Label className="text-base font-semibold text-foreground">
                  Middle of Term
                </Label>
                <p className="text-sm text-muted-foreground leading-snug">
                  What amount should a student be charged if they join in the middle of the term?
                </p>
              </div>
              <div className="shrink-0 w-full sm:w-[180px] space-y-3">
                <Select value={termMidFee} onValueChange={setTermMidFee}>
                  <SelectTrigger className="w-full bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors shadow-sm focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/40 rounded-md h-10">
                    <SelectValue placeholder="Select amount" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-lg rounded-md overflow-hidden z-[100]">
                    <SelectItem value="full" className="cursor-pointer py-2 focus:bg-indigo-50 focus:text-indigo-900 dark:focus:bg-indigo-500/20 dark:focus:text-indigo-100 transition-colors">Full Payment</SelectItem>
                    <SelectItem value="partial" className="cursor-pointer py-2 focus:bg-indigo-50 focus:text-indigo-900 dark:focus:bg-indigo-500/20 dark:focus:text-indigo-100 transition-colors">Partial (Custom)</SelectItem>
                    <SelectItem value="none" className="cursor-pointer py-2 focus:bg-indigo-50 focus:text-indigo-900 dark:focus:bg-indigo-500/20 dark:focus:text-indigo-100 transition-colors">None (Next Term)</SelectItem>
                  </SelectContent>
                </Select>

                {termMidFee === 'partial' && (
                  <div className="flex items-center gap-2 pl-1 animate-in slide-in-from-top-2 fade-in duration-200">
                    <Label className="text-sm text-muted-foreground whitespace-nowrap">Charge %:</Label>
                    <Input
                      type="number"
                      min="1"
                      max="99"
                      value={termMidPartial}
                      onChange={(e) => setTermMidPartial(e.target.value)}
                      className="w-16 h-8 text-center bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 px-1"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* End of term */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 p-4 rounded-lg border bg-current/5 hover:bg-current/10 border-gray-200 dark:border-gray-700 transition-colors">
              <div className="space-y-1 pr-6 flex-1">
                <Label className="text-base font-semibold text-foreground">
                  End of Term
                </Label>
                <p className="text-sm text-muted-foreground leading-snug">
                  What amount should a student be charged if they join at the end of the term?
                </p>
              </div>
              <div className="shrink-0 w-full sm:w-[180px] space-y-3">
                <Select value={termEndFee} onValueChange={setTermEndFee}>
                  <SelectTrigger className="w-full bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors shadow-sm focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/40 rounded-md h-10">
                    <SelectValue placeholder="Select amount" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-lg rounded-md overflow-hidden z-[100]">
                    <SelectItem value="full" className="cursor-pointer py-2 focus:bg-indigo-50 focus:text-indigo-900 dark:focus:bg-indigo-500/20 dark:focus:text-indigo-100 transition-colors">Full Payment</SelectItem>
                    <SelectItem value="partial" className="cursor-pointer py-2 focus:bg-indigo-50 focus:text-indigo-900 dark:focus:bg-indigo-500/20 dark:focus:text-indigo-100 transition-colors">Partial (Custom)</SelectItem>
                    <SelectItem value="none" className="cursor-pointer py-2 focus:bg-indigo-50 focus:text-indigo-900 dark:focus:bg-indigo-500/20 dark:focus:text-indigo-100 transition-colors">None (Next Term)</SelectItem>
                  </SelectContent>
                </Select>

                {termEndFee === 'partial' && (
                  <div className="flex items-center gap-2 pl-1 animate-in slide-in-from-top-2 fade-in duration-200">
                    <Label className="text-sm text-muted-foreground whitespace-nowrap">Charge %:</Label>
                    <Input
                      type="number"
                      min="1"
                      max="99"
                      value={termEndPartial}
                      onChange={(e) => setTermEndPartial(e.target.value)}
                      className="w-16 h-8 text-center bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 px-1"
                    />
                  </div>
                )}
              </div>
            </div>

          </CardContent>
          <div className="p-4 bg-gray-50/50 dark:bg-gray-900/50 border-t flex justify-end">
            <Button 
               onClick={() => setShowFeeConfirmDialog(true)} 
               disabled={savingAssessment}
               className="bg-indigo-600 hover:bg-indigo-700 text-white min-w-[140px]"
            >
              {savingAssessment ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
              ) : (
                "Save Automation Rules"
              )}
            </Button>
          </div>
        </Card>

      </div>

      {/* Styled Centered Alert Dialog for Confirmation */}
      <AlertDialog open={showFeeConfirmDialog} onOpenChange={setShowFeeConfirmDialog}>
        <AlertDialogContent className="sm:max-w-md bg-white dark:bg-gray-900 border-0 shadow-2xl rounded-xl overflow-hidden">
          <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-indigo-500 to-purple-600"></div>
          
          <AlertDialogHeader className="pt-4 pb-2">
            <div className="mx-auto w-12 h-12 bg-indigo-100 dark:bg-indigo-900/40 rounded-full flex items-center justify-center mb-4">
              <Calculator className="h-6 w-6 text-indigo-600 dark:text-indigo-500" />
            </div>
            <AlertDialogTitle className="text-center text-xl font-bold">
              Update Assessment Rules?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-gray-600 dark:text-gray-400 mt-2 text-sm leading-relaxed">
              These rules will dictate the prorated admission costs for all newly registered students dynamically based on the current term progression. Ensure the percentages are correct.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <AlertDialogFooter className="sm:justify-center gap-3 mt-6 pb-2">
            <AlertDialogCancel 
              disabled={savingAssessment}
              className="mt-0 sm:mt-0 px-6 font-medium rounded-lg border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={(e) => { e.preventDefault(); saveFeeAssessmentConfig(); }}
              disabled={savingAssessment}
              className="px-8 font-medium rounded-lg shadow-sm border-0 transition-all bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {savingAssessment ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Applied...</>
              ) : "Confirm & Save"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Styled Centered Alert Dialog for Cash Confirmation */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="sm:max-w-md bg-white dark:bg-gray-900 border-0 shadow-2xl rounded-xl overflow-hidden">
          <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-600"></div>
          
          <AlertDialogHeader className="pt-4 pb-2">
            <div className="mx-auto w-12 h-12 bg-amber-100 dark:bg-amber-900/40 rounded-full flex items-center justify-center mb-4">
              <ShieldAlert className="h-6 w-6 text-amber-600 dark:text-amber-500" />
            </div>
            <AlertDialogTitle className="text-center text-xl font-bold">
              {pendingCashState ? "Enable Cash Payments?" : "Disable Cash Payments?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-gray-600 dark:text-gray-400 mt-2 text-sm leading-relaxed">
              {pendingCashState 
                ? "Accountants will be able to receive and register physical cash transactions for student fees. Ensure proper auditing protocols are in place." 
                : "Cash payments will be blocked. Only registered digital deposits (M-Pesa, Bank Transfer) will be permitted for fee collection."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <AlertDialogFooter className="sm:justify-center gap-3 mt-6 pb-2">
            <AlertDialogCancel 
              disabled={saving}
              className="mt-0 sm:mt-0 px-6 font-medium rounded-lg border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={(e) => { e.preventDefault(); confirmToggle(); }}
              disabled={saving}
              className={`px-8 font-medium rounded-lg shadow-sm border-0 transition-all ${
                pendingCashState 
                  ? "bg-green-600 hover:bg-green-700 text-white" 
                  : "bg-red-600 hover:bg-red-700 text-white"
              }`}
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Applying...
                </>
              ) : (
                pendingCashState ? "Yes, Enable" : "Yes, Disable"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default FinanceSettings;
